import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const SUBJECTS = [
  { id: 'science', name: 'Science', icon: '⚙️' },
  { id: 'mathematics', name: 'Mathematics', icon: '✏️' },
  { id: 'social_science', name: 'Social Science', icon: '🌍' },
  { id: 'english', name: 'English', icon: '📖' },
  { id: 'hindi', name: 'Hindi', icon: '🔤' },
  { id: 'ai_ct', name: 'AI / CT', icon: '🧠' },
];

const CLASSES = [6, 7, 8, 9, 10];

const AdminContentPage = () => {
  const [selectedClass, setSelectedClass] = useState(10);
  const [selectedSubject, setSelectedSubject] = useState('science');
  const [syllabus, setSyllabus] = useState(null);
  const [generatedTopics, setGeneratedTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, generated: 0 });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicContent, setTopicContent] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const { success, error } = useToast();

  useEffect(() => { loadContent(); }, [selectedClass, selectedSubject]);

  const loadContent = async () => {
    setLoading(true);
    setSelectedTopic(null);
    setTopicContent(null);
    setEditMode(false);
    try {
      const [syllabusRes, statsRes] = await Promise.all([
        api.get(`/admin/content/syllabus/${selectedClass}/${selectedSubject}`),
        api.get(`/admin/content/stats/${selectedClass}/${selectedSubject}`)
      ]);
      setSyllabus(syllabusRes.data.data);
      setGeneratedTopics(statsRes.data.data?.generatedTopics || []);
      setStats(statsRes.data.data || { total: 0, generated: 0 });
    } catch {
      setSyllabus(null);
      setGeneratedTopics([]);
    } finally { setLoading(false); }
  };

  const loadTopicPreview = async (topicId) => {
    setSelectedTopic(topicId);
    setTopicLoading(true);
    setTopicContent(null);
    setEditMode(false);
    try {
      const res = await api.get(`/admin/content/topic/${topicId}`);
      setTopicContent(res.data.data);
    } catch { setTopicContent(null); }
    finally { setTopicLoading(false); }
  };

  const handleEdit = () => {
    setEditData(JSON.parse(JSON.stringify(topicContent)));
    setEditMode(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/topic/${selectedTopic}`, editData);
      success('Topic updated successfully!');
      setTopicContent(editData);
      setEditMode(false);
    } catch { error('Failed to save changes.'); }
    finally { setSaving(false); }
  };

  const updateBullet = (layerKey, idx, value) => {
    setEditData(prev => ({
      ...prev,
      [layerKey]: { ...prev[layerKey], bullets: prev[layerKey].bullets.map((b, i) => i === idx ? value : b) }
    }));
  };

  const pct = stats.total > 0 ? Math.round((stats.generated / stats.total) * 100) : 0;

  return (
    <AdminShell title="Content Library" subtitle="Browse, preview and edit CBSE content">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Class + Subject selectors */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px 16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>Class</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
            {CLASSES.map(c => (
              <button key={c} onClick={() => setSelectedClass(c)}
                style={{ padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: selectedClass === c ? 'none' : '1px solid #E2E8F0', background: selectedClass === c ? '#1A3C6E' : 'white', color: selectedClass === c ? 'white' : '#64748B', cursor: 'pointer' }}>
                Class {c}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>Subject</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {SUBJECTS.map(s => (
              <button key={s.id} onClick={() => setSelectedSubject(s.id)}
                style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: selectedSubject === s.id ? 'none' : '1px solid #E2E8F0', background: selectedSubject === s.id ? '#FF6B00' : 'white', color: selectedSubject === s.id ? 'white' : '#64748B', cursor: 'pointer' }}>
                {s.icon} {s.name}
              </button>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
            <span>Class {selectedClass} — {SUBJECTS.find(s => s.id === selectedSubject)?.name}</span>
            <span style={{ color: pct === 100 ? '#1B7F3E' : '#FF6B00' }}>{stats.generated}/{stats.total} topics ({pct}%)</span>
          </div>
          <div style={{ background: '#E2E8F0', borderRadius: '10px', height: '8px' }}>
            <div style={{ background: pct === 100 ? '#1B7F3E' : '#FF6B00', borderRadius: '10px', height: '100%', width: `${pct}%`, transition: 'width 0.3s' }}></div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selectedTopic ? '1fr 1.2fr' : '1fr', gap: '14px' }}>

          {/* Topic list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>Loading...</div>
            ) : !syllabus ? (
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748B' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                <div style={{ fontWeight: 700 }}>No syllabus found</div>
              </div>
            ) : syllabus.chapters?.map((ch, idx) => (
              <div key={ch.id} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', background: '#F7F8FC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#1A3C6E', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800 }}>{idx + 1}</div>
                  <div style={{ fontSize: '13px', fontWeight: 800, flex: 1 }}>{ch.name}</div>
                  <div style={{ fontSize: '11px', color: '#64748B' }}>{ch.topics.filter(t => generatedTopics.includes(t.id)).length}/{ch.topics.length}</div>
                </div>
                <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {ch.topics.map(t => {
                    const isGenerated = generatedTopics.includes(t.id);
                    const isSelected = selectedTopic === t.id;
                    return (
                      <div key={t.id} onClick={() => isGenerated && loadTopicPreview(t.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderRadius: '8px', cursor: isGenerated ? 'pointer' : 'default', background: isSelected ? '#FFF3E0' : 'transparent', border: isSelected ? '1px solid #FF6B00' : '1px solid transparent' }}>
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: isGenerated ? '#1B7F3E' : '#E2E8F0', flexShrink: 0 }}></div>
                        <div style={{ fontSize: '12px', fontWeight: 600, flex: 1, color: isGenerated ? '#1A1A2E' : '#94A3B8' }}>{t.name}</div>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '20px', background: isGenerated ? '#E6F4EC' : '#F7F8FC', color: isGenerated ? '#1B7F3E' : '#94A3B8' }}>
                          {isGenerated ? '✓' : '⏳'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Preview / Edit panel */}
          {selectedTopic && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800 }}>
                  {editMode ? '✏️ Edit Topic' : '📖 Preview'}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {!editMode ? (
                    <>
                      <button onClick={handleEdit} style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '5px 12px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>✏️ Edit</button>
                      <button onClick={() => { setSelectedTopic(null); setTopicContent(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748B' }}>×</button>
                    </>
                  ) : (
                    <>
                      <button onClick={handleSave} disabled={saving} style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '8px', padding: '5px 12px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>{saving ? 'Saving...' : '✓ Save'}</button>
                      <button onClick={() => setEditMode(false)} style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '8px', padding: '5px 12px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>✕ Cancel</button>
                    </>
                  )}
                </div>
              </div>

              {topicLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</div>
              ) : topicContent ? (
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '2px' }}>{topicContent.topic_name}</div>
                  <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '16px' }}>{topicContent.subject} · {topicContent.chapter_name} · {selectedTopic}</div>

                  {!editMode ? (
                    // PREVIEW MODE
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#FF6B00', textTransform: 'uppercase', marginBottom: '8px' }}>Layer 1 — Key Points</div>
                      {topicContent.layer_1?.bullets?.map((b, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', fontSize: '12px', lineHeight: 1.5 }}>
                          <span style={{ color: '#FF6B00', flexShrink: 0 }}>•</span><span>{b}</span>
                        </div>
                      ))}
                      {topicContent.layer_1?.example && (
                        <div style={{ marginTop: '10px', background: '#E6F4EC', borderRadius: '8px', padding: '10px', fontSize: '12px', color: '#1B7F3E', lineHeight: 1.5, marginBottom: '14px' }}>
                          💡 {topicContent.layer_1.example}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#1A3C6E', textTransform: 'uppercase', marginBottom: '8px', marginTop: '12px' }}>Layer 2 — Deeper</div>
                      <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#64748B', marginBottom: '14px' }}>{topicContent.layer_2?.explanation || 'No Layer 2 content'}</p>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', marginBottom: '8px' }}>Layer 3 — Simplest</div>
                      <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#64748B', marginBottom: '14px' }}>{topicContent.layer_3?.simple_breakdown || 'No Layer 3 content'}</p>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', marginBottom: '4px' }}>Quiz</div>
                      <div style={{ fontSize: '12px', color: '#1A1A2E' }}>{topicContent.quiz_sets?.length || 0} sets · {topicContent.quiz_sets?.[0]?.questions?.length || 0} questions each</div>
                    </div>
                  ) : (
                    // EDIT MODE
                    <div>
                      {/* Layer 1 bullets */}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#FF6B00', textTransform: 'uppercase', marginBottom: '8px' }}>Layer 1 — Bullet Points</div>
                      {editData?.layer_1?.bullets?.map((b, i) => (
                        <textarea key={i} value={b} onChange={e => updateBullet('layer_1', i, e.target.value)}
                          style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '6px', resize: 'vertical', minHeight: '50px', outline: 'none', boxSizing: 'border-box' }} />
                      ))}

                      {/* Layer 1 example */}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#1B7F3E', textTransform: 'uppercase', marginBottom: '6px', marginTop: '10px' }}>Real-life Example</div>
                      <textarea value={editData?.layer_1?.example || ''} onChange={e => setEditData(prev => ({ ...prev, layer_1: { ...prev.layer_1, example: e.target.value } }))}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '10px', resize: 'vertical', minHeight: '60px', outline: 'none', boxSizing: 'border-box' }} />

                      {/* Layer 2 */}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#1A3C6E', textTransform: 'uppercase', marginBottom: '6px', marginTop: '10px' }}>Layer 2 — Deeper Explanation</div>
                      <textarea value={editData?.layer_2?.explanation || ''} onChange={e => setEditData(prev => ({ ...prev, layer_2: { ...prev.layer_2, explanation: e.target.value } }))}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '10px', resize: 'vertical', minHeight: '80px', outline: 'none', boxSizing: 'border-box' }} />

                      {/* Layer 3 */}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', marginBottom: '6px', marginTop: '10px' }}>Layer 3 — Simple Breakdown</div>
                      <textarea value={editData?.layer_3?.simple_breakdown || ''} onChange={e => setEditData(prev => ({ ...prev, layer_3: { ...prev.layer_3, simple_breakdown: e.target.value } }))}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '6px', resize: 'vertical', minHeight: '80px', outline: 'none', boxSizing: 'border-box' }} />

                      {/* Layer 3 analogy */}
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', marginBottom: '6px', marginTop: '10px' }}>Layer 3 — Daily Life Analogy</div>
                      <textarea value={editData?.layer_3?.analogy || ''} onChange={e => setEditData(prev => ({ ...prev, layer_3: { ...prev.layer_3, analogy: e.target.value } }))}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '10px', resize: 'vertical', minHeight: '60px', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Failed to load topic</div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminContentPage;
